/**
 * Centralised JSON-LD schema builders for Perceptica.
 *
 * Every helper returns a plain object ready to be JSON.stringify'd inside a
 * route's `head().scripts` entry. Helpers are locale-aware where it matters
 * (Article, Report, BreadcrumbList) and locale-agnostic where the entity is
 * sitewide (Organization, WebSite).
 */

export type Locale = "en" | "bg";

import { EDITORIAL_TEAM_NAME, findAuthor, REVIEWER, type PostAuthor } from "@/content/authors";

export const SITE_ORIGIN = "https://perceptica.com";
export const ORG_ID = `${SITE_ORIGIN}/#organization`;
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

const LOGO_URL = `${SITE_ORIGIN}/perceptica-logo.png`;

const SAME_AS = [
  "https://www.linkedin.com/company/perceptica-media-intelligence/",
  "https://www.facebook.com/PercepticaAnalytics",
  "https://x.com/PercepticaA",
  "https://www.google.com/search?kgmid=/g/11p9n_ghq4",
];

const HAS_MAP = "https://share.google/Np6VgZbXLOXQZSrYL";

const KEYWORDS_EN = [
  "media monitoring",
  "media monitoring platform",
  "media listening",
  "media listening platform",
  "media monitoring services",
  "media intelligence",
  "media analysis",
  "media analyses",
  "market research",
  "reputation analysis",
  "competitive intelligence",
  "competitive intelligence analysis",
  "crisis monitoring",
  "crisis analysis",
  "employer brand analysis",
  "employer branding analysis",
  "media monitoring in SEE",
  "media monitoring in Southeast Europe",
];

const KEYWORDS_BG = [
  "медиен мониторинг",
  "платформа за медиен мониторинг",
  "медийно разузнаване",
  "услуги за медиен мониторинг",
  "медийни анализи",
  "медиен анализ",
  "пазарно проучване",
  "кризисен мониторинг",
  "кризисен анализ",
  "анализ при кризи",
  "анализ на репутацията",
  "конкурентно разузнаване",
  "анализ на работодател",
  "анализ на бранд",
  "анализ на работодателски бранд",
];

const KNOWS_ABOUT = [
  "Media monitoring",
  "Media intelligence",
  "Media analysis",
  "Media listening",
  "Reputation analysis",
  "Competitive intelligence",
  "Crisis monitoring",
  "Employer branding analysis",
  "Market research",
];

const AREA_SERVED = [
  { "@type": "Country", name: "Bulgaria" },
  { "@type": "Country", name: "Romania" },
  { "@type": "Country", name: "Greece" },
  { "@type": "Country", name: "Serbia" },
  { "@type": "Country", name: "North Macedonia" },
  { "@type": "Country", name: "Albania" },
  { "@type": "Country", name: "Croatia" },
  { "@type": "Country", name: "Slovenia" },
  { "@type": "Country", name: "Bosnia and Herzegovina" },
  { "@type": "Country", name: "Montenegro" },
  { "@type": "Country", name: "Kosovo" },
  { "@type": "Place", name: "Southeast Europe" },
];

/** Industry bodies and associations Perceptica belongs to. */
const MEMBER_OF = [
  {
    "@type": "Organization",
    name: "AMEC - International Association for the Measurement and Evaluation of Communication",
    alternateName: "AMEC",
    url: "https://amecorg.com/",
  },
  {
    "@type": "Organization",
    name: "FIBEP - Federation Internationale des Bureaux d'Extraits de Presse",
    alternateName: "FIBEP",
    url: "https://www.fibep.info/",
  },
  {
    "@type": "Organization",
    name: "Сдружение за знание-интензивно развитие",
    alternateName: ["СЗИР", "AIBEST"],
    url: "https://aibest.bg/",
  },
  {
    "@type": "Organization",
    name: "Updata One",
    url: "https://updataone.com/",
  },
  {
    "@type": "Organization",
    name: "Българско дружество за връзки с обществеността",
    alternateName: ["БДВО", "Bulgarian Public Relations Society"],
    url: "https://bdvo.org/",
  },
];

/** Sitewide Organization block. Placed once at the root. */
export function orgSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "Perceptica",
    legalName: "Персептика ЕООД",
    alternateName: [
      "Персептика",
      "Персептика ЕООД",
      "Персептика България",
      "Perceptica EOOD",
      "Perceptica LTD",
      "Perceptica Media Monitoring",
      "Perceptica.com",
    ],
    url: SITE_ORIGIN,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
      width: 512,
      height: 512,
    },
    image: LOGO_URL,
    email: "sales@perceptica.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "117 Hristo Botev Blvd.",
      addressLocality: "Sofia",
      postalCode: "1303",
      addressCountry: "BG",
    },
    foundingDate: "2012",
    vatID: "BG202876485",
    taxID: "202876485",
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "sales@perceptica.com",
        availableLanguage: ["English", "Bulgarian"],
        areaServed: ["BG", "RO", "GR", "RS", "MK", "AL", "HR", "SI", "BA", "ME", "XK"],
      },
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "sales@perceptica.com",
        availableLanguage: ["English", "Bulgarian"],
      },
    ],
    areaServed: AREA_SERVED,
    knowsAbout: KNOWS_ABOUT,
    memberOf: MEMBER_OF,
    keywords: [...KEYWORDS_EN, ...KEYWORDS_BG].join(", "),
    hasMap: HAS_MAP,
    sameAs: SAME_AS,
    hasOfferCatalog: offerCatalog(),
  };
}

/**
 * Every service Perceptica sells, as one catalogue hung off the Organization
 * node so search engines see them as a single portfolio rather than islands.
 */
export const SERVICE_CATALOG: {
  name: string;
  path: string;
  serviceType: string;
  serviceOutput?: string;
}[] = [
  {
    name: "Media monitoring",
    path: "/media-monitoring",
    serviceType: "Media monitoring",
    serviceOutput: "Media monitoring feed and alerts",
  },
  {
    name: "Media monitoring platform",
    path: "/media-monitoring/platform",
    serviceType: "Media monitoring platform",
    serviceOutput: "Media listening platform access",
  },
  {
    name: "Media monitoring coverage",
    path: "/media-monitoring/coverage",
    serviceType: "Media monitoring services",
    serviceOutput: "Media coverage dataset",
  },
  {
    name: "Media analysis",
    path: "/media-analysis",
    serviceType: "Media analysis",
    serviceOutput: "Media analysis report",
  },
  {
    name: "Reputation analysis",
    path: "/media-analysis/reputation",
    serviceType: "Reputation analysis",
    serviceOutput: "Reputation analysis report",
  },
  {
    name: "Competitive intelligence analysis",
    path: "/media-analysis/competitive-intelligence",
    serviceType: "Competitive intelligence analysis",
    serviceOutput: "Competitive intelligence report",
  },
  {
    name: "Crisis monitoring",
    path: "/media-analysis/crisis-monitoring",
    serviceType: "Crisis monitoring",
    serviceOutput: "Crisis monitoring and analysis report",
  },
  {
    name: "Employer branding analysis",
    path: "/media-analysis/employer-branding",
    serviceType: "Employer branding analysis",
    serviceOutput: "Employer brand analysis report",
  },
  {
    name: "Market research",
    path: "/media-analysis/market-research",
    serviceType: "Market research",
    serviceOutput: "Market research report",
  },
  {
    name: "Custom media research and sector reporting",
    path: "/reports",
    serviceType: "Custom Media Research & Sector Reporting",
    serviceOutput: "Custom sector research report",
  },
];

function offerCatalog() {
  return {
    "@type": "OfferCatalog",
    "@id": `${SITE_ORIGIN}/#services`,
    name: "Perceptica media intelligence services",
    itemListElement: SERVICE_CATALOG.map((s, i) => ({
      "@type": "Offer",
      position: i + 1,
      url: `${SITE_ORIGIN}${s.path}`,
      itemOffered: {
        "@type": "Service",
        "@id": `${SITE_ORIGIN}${s.path}#service`,
        name: s.name,
        serviceType: s.serviceType,
        provider: { "@id": ORG_ID },
      },
    })),
  };
}

/** Sitewide WebSite block. Placed once at the root. */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: "Perceptica",
    alternateName: ["Персептика", "Perceptica Media Monitoring"],
    url: SITE_ORIGIN,
    inLanguage: ["en", "bg"],
    publisher: { "@id": ORG_ID },
  };
}

export interface BreadcrumbItem {
  name: string;
  /** Path with leading slash (e.g. "/media-monitoring"). Locale prefix applied automatically. */
  path: string;
}

/** Build a BreadcrumbList. Always prepends Home. */
export function breadcrumbSchema(locale: Locale, trail: BreadcrumbItem[]) {
  const home: BreadcrumbItem =
    locale === "bg" ? { name: "Начало", path: "/" } : { name: "Home", path: "/" };
  const all = [home, ...trail];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: all.map((item, i) => {
      const localized =
        locale === "bg"
          ? item.path === "/"
            ? "/bg"
            : `/bg${item.path}`
          : item.path;
      return {
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        item: `${SITE_ORIGIN}${localized === "/" ? "" : localized}`,
      };
    }),
  };
}

export interface ServiceSchemaInput {
  locale: Locale;
  name: string;
  description: string;
  serviceType: string;
  url: string; // canonical URL of the service page
  category?: string;
  /** Deliverable produced by the service (e.g. "Media analysis report"). */
  serviceOutput?: string | string[];
  /** Audience refinement for sector (industry) and role (audience) pages. */
  audience?: {
    audienceType?: string;
    /** Sector this variant targets, e.g. "Pharmaceutical industry". */
    industry?: string;
    /** Professional role this variant targets, e.g. "PR agencies". */
    role?: string;
  };
  /** Sibling services, as absolute URLs, linked via isSimilarTo. */
  isSimilarTo?: string[];
}

export function serviceSchema(s: ServiceSchemaInput) {
  const outputs = s.serviceOutput
    ? (Array.isArray(s.serviceOutput) ? s.serviceOutput : [s.serviceOutput]).map((name) => ({
        "@type": "CreativeWork",
        name,
      }))
    : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${s.url.split("#")[0]}#service`,
    name: s.name,
    description: s.description,
    serviceType: s.serviceType,
    category: s.category ?? s.serviceType,
    provider: { "@id": ORG_ID },
    areaServed: AREA_SERVED,
    availableLanguage: ["English", "Bulgarian"],
    url: s.url,
    inLanguage: s.locale,
    ...(outputs ? { serviceOutput: outputs.length === 1 ? outputs[0] : outputs } : {}),
    ...(s.isSimilarTo?.length
      ? { isSimilarTo: s.isSimilarTo.map((url) => ({ "@type": "Service", "@id": `${url}#service`, url })) }
      : {}),
    audience: {
      "@type": "BusinessAudience",
      audienceType:
        s.audience?.role ??
        s.audience?.audienceType ??
        "Communications, PR, marketing, brand and HR teams",
      ...(s.audience?.industry ? { industry: s.audience.industry } : {}),
    },
    isPartOf: { "@id": `${SITE_ORIGIN}/#services` },
  };
}

export interface ArticleSchemaInput {
  locale: Locale;
  url: string;
  headline: string;
  description: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  /** Explicit Person author; takes precedence over `authorName` lookup. */
  author?: PostAuthor;
}

function personNode(p: PostAuthor) {
  return {
    "@type": "Person",
    "@id": p.url ? `${p.url}-${p.name.toLowerCase().replace(/\s+/g, "-")}` : undefined,
    name: p.name,
    ...(p.url ? { url: p.url } : {}),
    ...(p.jobTitle ? { jobTitle: p.jobTitle } : {}),
    ...(p.sameAs?.length ? { sameAs: p.sameAs } : {}),
    worksFor: { "@id": ORG_ID },
  };
}

function editorialTeamNode(locale: Locale) {
  return {
    "@type": "Organization",
    "@id": `${SITE_ORIGIN}/#editorial-team`,
    name: EDITORIAL_TEAM_NAME,
    description:
      locale === "bg"
        ? "Редакционният екип на Perceptica се състои от маркетинг специалисти и медийни анализатори, а идеите се одобряват от висшия ръководен екип на Perceptica."
        : "The Perceptica Editorial Team consists of marketing specialists and media analysts, with ideas approved by Perceptica's C-level team.",
    url: `${SITE_ORIGIN}/about-us`,
    logo: { "@type": "ImageObject", url: LOGO_URL, width: 512, height: 512 },
    parentOrganization: { "@id": ORG_ID },
  };
}

/**
 * Resolves the author node: known analysts become a proper `Person`, while the
 * brand ("Perceptica") or an unknown byline maps to the Organization node.
 */
function authorNode(a: ArticleSchemaInput) {
  if (a.authorName?.trim().toLowerCase() === EDITORIAL_TEAM_NAME.toLowerCase()) {
    return editorialTeamNode(a.locale);
  }
  const person = a.author ?? findAuthor(a.authorName);
  return person ? personNode(person) : { "@id": ORG_ID };
}

/**
 * BlogPosting variant for blog article pages. Self-contained for rich results:
 * the publisher Organization node is inlined (same @id as the sitewide node, so
 * consumers merge rather than duplicate it).
 */
export function blogPostingSchema(a: ArticleSchemaInput) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${a.url}#blogposting`,
    mainEntityOfPage: { "@type": "WebPage", "@id": a.url },
    url: a.url,
    headline: a.headline.length > 110 ? `${a.headline.slice(0, 107).trim()}...` : a.headline,
    name: a.headline,
    description: a.description,
    inLanguage: a.locale,
    isPartOf: { "@id": WEBSITE_ID },
    ...(a.image ? { image: [a.image] } : {}),
    ...(a.datePublished ? { datePublished: a.datePublished } : {}),
    ...(a.dateModified ? { dateModified: a.dateModified } : {}),
    author: authorNode(a),
    reviewedBy: personNode(REVIEWER),
    publisher: {
      "@type": "Organization",
      "@id": ORG_ID,
      name: "Perceptica",
      url: SITE_ORIGIN,
      logo: { "@type": "ImageObject", url: LOGO_URL, width: 512, height: 512 },
      sameAs: SAME_AS,
    },
    keywords: (a.locale === "bg" ? KEYWORDS_BG : KEYWORDS_EN).join(", "),
  };
}

export interface ReportSchemaInput {
  locale: Locale;
  url: string;
  title: string;
  description: string;
  image?: string;
  year?: string;
  topic?: string;
}

export interface ReportDocSchemaInput extends ReportSchemaInput {
  /** Last substantive update, ISO or year. Falls back to datePublished. */
  dateModified?: string;
  /** Direct URL of the PDF when one is publicly addressable. */
  pdfUrl?: string;
  /** Exact publish date (YYYY-MM-DD); preferred over the year. */
  datePublished?: string;
  /** Named analyst; added as a Person author alongside Perceptica. */
  analyst?: { name: string; role?: string };
}

/**
 * Named research entity that authors every Perceptica report. It is a
 * sub-organization of the brand rather than the brand node itself, so the
 * authorship signal is specific without inventing a fake Person.
 */
export const RESEARCH_DESK_ID = `${SITE_ORIGIN}/#research-desk`;

function researchDeskNode(locale: Locale) {
  return {
    "@type": "Organization",
    "@id": RESEARCH_DESK_ID,
    name: locale === "bg" ? "Perceptica Research Desk" : "Perceptica Research Desk",
    alternateName: locale === "bg" ? "Изследователски екип на Perceptica" : "Perceptica Research Team",
    url: `${SITE_ORIGIN}/about-us#team`,
    parentOrganization: { "@id": ORG_ID },
  };
}

/** Year-only metadata becomes a valid ISO date so freshness signals parse. */
function isoFromYear(year?: string): string | undefined {
  if (!year) return undefined;
  if (/^\d{4}-\d{2}-\d{2}/.test(year)) return year;
  if (/^\d{4}$/.test(year)) return `${year}-01-01`;
  return undefined;
}

export interface BlogListSchemaInput {
  locale: Locale;
  /** Canonical URL of the blog list page. */
  url: string;
  name: string;
  description: string;
  posts?: { name: string; url: string; datePublished?: string }[];
}

/**
 * `Blog` node for the blog list pages. Anchored to the sitewide WebSite node
 * via `isPartOf` so the graph stays consistent with single-post pages.
 */
export function blogListSchema(b: BlogListSchemaInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${b.url}#blog`,
    url: b.url,
    name: b.name,
    description: b.description,
    inLanguage: b.locale,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    ...(b.posts && b.posts.length
      ? {
          blogPost: b.posts.map((p) => ({
            "@type": "BlogPosting",
            "@id": `${p.url}#blogposting`,
            headline: p.name,
            url: p.url,
            ...(p.datePublished ? { datePublished: p.datePublished } : {}),
            publisher: { "@id": ORG_ID },
          })),
        }
      : {}),
  };
}

/**
 * Article/Report node for a single report landing page.
 *
 * Includes the downloadable PDF as `associatedMedia` (a DataDownload /
 * MediaObject), ISO dates, `mainEntityOfPage`, the named research-desk author
 * and the lead-gating pattern Google expects for form-gated assets
 * (`isAccessibleForFree: false` + a paywalled `hasPart`).
 */
export function reportSchema(r: ReportDocSchemaInput) {
  const published = isoFromYear(r.datePublished) ?? isoFromYear(r.year);
  const modified = isoFromYear(r.dateModified) ?? published;
  const pdfName =
    r.locale === "bg" ? `${r.title} (PDF доклад)` : `${r.title} (PDF report)`;
  return {
    "@context": "https://schema.org",
    "@type": ["Article", "Report"],
    "@id": `${r.url}#report`,
    mainEntityOfPage: { "@type": "WebPage", "@id": r.url },
    url: r.url,
    headline: r.title.length > 110 ? `${r.title.slice(0, 107).trim()}...` : r.title,
    name: r.title,
    description: r.description,
    inLanguage: r.locale === "bg" ? "bg-BG" : "en-GB",
    isPartOf: { "@id": WEBSITE_ID },
    ...(r.image ? { image: [r.image] } : {}),
    ...(published ? { datePublished: published } : {}),
    ...(modified ? { dateModified: modified } : {}),
    ...(r.topic ? { about: { "@type": "Thing", name: r.topic } } : {}),
    author: [
      ...(r.analyst
        ? [{ "@type": "Person", name: r.analyst.name, ...(r.analyst.role ? { jobTitle: r.analyst.role } : {}), worksFor: { "@id": ORG_ID } }]
        : []),
      { "@type": "Organization", "@id": ORG_ID, name: "Perceptica", url: SITE_ORIGIN },
      researchDeskNode(r.locale),
    ],
    reviewedBy: {
      "@type": "Person",
      name: REVIEWER.name,
      ...(REVIEWER.url ? { url: REVIEWER.url } : {}),
      ...(REVIEWER.jobTitle ? { jobTitle: REVIEWER.jobTitle } : {}),
      worksFor: { "@id": ORG_ID },
    },
    publisher: { "@id": ORG_ID },
    // Lead-gated asset: the full document sits behind a form.
    isAccessibleForFree: false,
    hasPart: {
      "@type": "WebPageElement",
      isAccessibleForFree: false,
      cssSelector: "#report-download",
    },
    associatedMedia: {
      "@type": "DataDownload",
      "@id": `${r.url}#pdf`,
      name: pdfName,
      encodingFormat: "application/pdf",
      contentUrl: r.pdfUrl ?? r.url,
      url: r.url,
      inLanguage: r.locale,
      isAccessibleForFree: false,
      ...(published ? { datePublished: published } : {}),
      creator: { "@id": RESEARCH_DESK_ID },
      publisher: { "@id": ORG_ID },
    },
    workExample: {
      "@type": "DigitalDocument",
      "@id": `${r.url}#document`,
      name: r.title,
      description: r.description,
      encodingFormat: "application/pdf",
      inLanguage: r.locale,
      url: r.url,
      isAccessibleForFree: false,
      author: { "@id": RESEARCH_DESK_ID },
      publisher: { "@id": ORG_ID },
      ...(published ? { datePublished: published } : {}),
    },
    keywords: (r.locale === "bg" ? KEYWORDS_BG : KEYWORDS_EN).join(", "),
  };
}

/** ItemList of every published report, for the /reports index pages. */
export function reportListSchema(input: {
  locale: Locale;
  url: string;
  reports: { name: string; url: string; year?: string; image?: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${input.url}#reports`,
    name: input.locale === "bg" ? "Доклади на Perceptica" : "Perceptica reports",
    inLanguage: input.locale,
    isPartOf: { "@id": WEBSITE_ID },
    numberOfItems: input.reports.length,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    itemListElement: input.reports.map((r, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: r.url,
      item: {
        "@type": ["Article", "Report"],
        "@id": `${r.url}#report`,
        name: r.name,
        url: r.url,
        ...(r.image ? { image: [r.image] } : {}),
        ...(isoFromYear(r.year) ? { datePublished: isoFromYear(r.year) } : {}),
        isAccessibleForFree: false,
        author: { "@id": RESEARCH_DESK_ID },
        publisher: { "@id": ORG_ID },
      },
    })),
  };
}

export function contactPageSchema(locale: Locale, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    url,
    inLanguage: locale,
    name: locale === "bg" ? "Свържете се с Perceptica" : "Contact Perceptica",
    about: { "@id": ORG_ID },
    mainEntity: { "@id": ORG_ID },
  };
}

export function faqSchema(items: { q: string; a: string }[], locale: Locale) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: locale,
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function softwareAppSchema(locale: Locale, url: string) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Perceptica Media Monitoring Platform",
    alternateName: locale === "bg" ? "Платформа за медиен мониторинг Perceptica" : undefined,
    description:
      locale === "bg"
        ? "Платформа за медиен мониторинг и медийно разузнаване в Югоизточна Европа - мониторинг на спомeнавания, анализ на тоналността, кризисни сигнали и доклади."
        : "Media monitoring and media intelligence platform for Southeast Europe - track mentions, sentiment, crisis alerts and analyst-grade reports.",
    url,
    inLanguage: locale,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Media Monitoring",
    operatingSystem: "Web",
    provider: { "@id": ORG_ID },
    featureList: [
      "Media monitoring across press, broadcast, online news and social",
      "Sentiment and reach scoring on every mention",
      "Crisis anomaly detection and analyst escalation",
      "Reputation, competitive and employer brand analysis",
      "Custom dashboards and analyst-written reports",
    ],
  };
}

export interface CollectionPageInput {
  locale: Locale;
  url: string;
  name: string;
  description: string;
  hasPart?: { name: string; url: string }[];
}

export function collectionPageSchema(c: CollectionPageInput) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: c.name,
    description: c.description,
    url: c.url,
    inLanguage: c.locale,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    ...(c.hasPart && c.hasPart.length
      ? {
          hasPart: c.hasPart.map((p) => ({
            "@type": "WebPage",
            name: p.name,
            url: p.url,
          })),
        }
      : {}),
  };
}

/** Helper: turn schema objects into ready-to-spread head().scripts entries. */
export function asJsonLd(...nodes: Array<Record<string, unknown> | undefined | false | null>) {
  return nodes
    .filter((n): n is Record<string, unknown> => Boolean(n))
    .map((node) => ({
      type: "application/ld+json" as const,
      children: JSON.stringify(node),
    }));
}

/** Build a fully-qualified URL for a given locale + language-agnostic path. */
export function urlFor(locale: Locale, path: string): string {
  const p = path === "/" ? "" : path;
  if (locale === "bg") {
    if (p === "") return `${SITE_ORIGIN}/bg`;
    return `${SITE_ORIGIN}/bg${p}`;
  }
  return `${SITE_ORIGIN}${p}`;
}